import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import {
  ROUTE_EDIT_USER_IDEA,
  ROUTE_CREATE_CRITERIA,
} from '@/constants/routes';
import { slugify, unslugify } from '@/utilities/slugify';
import { push, ReceivedAction } from 'redux-first-router';
import type {
  CountryISO,
  PrimaryCountryISOElseGlobal,
} from '@/constants/countries';
import { checkSupportedMarket, getReadableCountry } from '@/utilities/markets';
import { getPath } from '@/router/utils';
import { preFetchCompany as preFetchCompanyRoutine } from '@/pages/CompanyReport/redux/routines';
import { useCallback } from 'react';

interface Location {
  id: string;
  marketISO?: CountryISO;
  market?: PrimaryCountryISOElseGlobal;
  industrySlug?: string;
  industryName: string;
  demonymCountry: string;
}

interface ReduxState extends Omit<Location, 'type'> {
  navigateTo: (id?: string, name?: string) => void;
  preFetchCompany: (id: string) => void;
}

export function useReduxStateForScreener(): ReduxState {
  const { id, marketISO, industrySlug, industryName, demonymCountry } =
    useSelector<
      {
        location: LocationState;
      },
      Location
    >((state) => {
      const { id, marketISO, market, industrySlug } = state.location.payload;
      const demonymCountry =
        typeof marketISO !== 'undefined'
          ? getReadableCountry(marketISO || 'global')
          : '';
      const industryName = unslugify(industrySlug);
      return {
        id,
        marketISO,
        market: checkSupportedMarket(market),
        industrySlug,
        industryName,
        demonymCountry,
      };
    }, shallowEqual);

  const navigateTo = (id?: string, name?: string) => {
    let action: ReceivedAction = {
      type: ROUTE_CREATE_CRITERIA,
      payload: {},
    };
    if (typeof id !== 'undefined') {
      action = {
        type: ROUTE_EDIT_USER_IDEA,
        payload: { id, name: slugify(name) },
      };
    }
    const path = getPath(action);
    push(path);
  };

  const dispatch = useDispatch();

  const preFetchCompany = useCallback(
    (companyId: string) => {
      dispatch(preFetchCompanyRoutine.trigger({ id: companyId }));
    },
    [dispatch]
  );

  return {
    id,
    marketISO,
    industrySlug,
    industryName,
    demonymCountry,
    navigateTo,
    preFetchCompany,
  };
}
